import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiZap, FiTarget, FiTrendingUp, FiShield, FiCheck } = FiIcons;

const IntroductionSection = () => {
  const replacedTools = [
    "CRM Software", "Email Marketing", "WhatsApp Tools", "Funnel Builder",
    "Website Builder", "Appointment Booking", "Video Hosting", "Analytics",
    "Survey Tools", "Webinar Platform", "Community Platform", "E-commerce"
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-10 right-10 w-32 h-32 bg-blue-100 rounded-full opacity-50"></div>
      <div className="absolute bottom-10 left-10 w-40 h-40 bg-purple-100 rounded-full opacity-30"></div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Your Complete Business Growth System — 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> All in One Place</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            FunnelsDone + AIFunnels replaces <strong className="text-blue-600">20+ expensive tools</strong> with one powerful, integrated platform designed specifically for MSME success.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Visual Representation */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 border border-blue-200">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
                  <SafeIcon icon={FiZap} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">FunnelsDone + AIFunnels</h3>
                <p className="text-gray-600">One Platform. Unlimited Growth.</p>
              </div>

              {/* Feature Icons Grid */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: FiTarget, label: "Funnels", color: "blue" },
                  { icon: FiTrendingUp, label: "CRM", color: "green" },
                  { icon: FiShield, label: "WhatsApp", color: "purple" },
                  { icon: FiZap, label: "Automation", color: "yellow" },
                  { icon: FiTarget, label: "Analytics", color: "red" },
                  { icon: FiTrendingUp, label: "Webinars", color: "indigo" }
                ].map((feature, index) => (
                  <motion.div 
                    key={index}
                    className={`bg-white p-4 rounded-xl shadow-sm border text-center hover:shadow-lg transition-all duration-300`}
                    whileHover={{ scale: 1.05, y: -2 }}
                  >
                    <SafeIcon icon={feature.icon} className={`w-8 h-8 text-${feature.color}-500 mx-auto mb-2`} />
                    <p className="text-sm font-medium text-gray-700">{feature.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: Benefits List */}
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Stop Juggling Multiple Tools
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">Unified Dashboard</h4>
                    <p className="text-gray-600">Manage everything from one central location - no more switching between apps</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">Seamless Integration</h4>
                    <p className="text-gray-600">All tools work together perfectly - no integration headaches</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">Cost Effective</h4>
                    <p className="text-gray-600">Save thousands monthly on subscription fees and agency costs</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">MSME Focused</h4>
                    <p className="text-gray-600">Built specifically for Indian MSMEs with local language support</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Replaced Tools */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">Replaces These Expensive Tools:</h4>
              <div className="grid grid-cols-2 gap-2">
                {replacedTools.map((tool, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-gray-700 line-through">{tool}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default IntroductionSection;